import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  fetchAuthConfig,
  loginWithBootstrapToken,
  loginWithEmailCode,
  requestEmailCode,
  rutaDeVuelta,
} from '../api';

export function LoginPage(): JSX.Element {
  const [params] = useSearchParams();
  // A dónde volver al entrar: p. ej. la pantalla de consentimiento de Claude, que mandó aquí
  // porque no había sesión. Solo rutas del propio portal.
  const volver = rutaDeVuelta(params.get('volver'));
  const client = useQueryClient();
  const config = useQuery({ queryKey: ['auth-config'], queryFn: fetchAuthConfig });

  const [token, setToken] = useState('');
  // La cuenta viene en el enlace, así que se valida antes de enseñarla: solo caracteres
  // de nombre de usuario de GitHub y como mucho 39, que es su límite. React ya escapa el
  // texto, pero sin esta comprobación cualquiera podría fabricar un enlace a esta pantalla
  // con un mensaje inventado dentro y usarlo para engañar a alguien.
  const cuenta = params.get('cuenta') ?? '';
  const cuentaSegura = /^[A-Za-z\d](?:[A-Za-z\d]|-(?=[A-Za-z\d])){0,38}$/.test(cuenta)
    ? cuenta
    : null;
  const [error, setError] = useState<string | null>(
    params.get('error') === 'no-autorizado'
      ? cuentaSegura
        ? `La cuenta «${cuentaSegura}» todavía no tiene acceso al portal. Pídeselo a un administrador: ` +
          'te lo da en un momento. Si tienes más de una cuenta de GitHub, comprueba que esta es la ' +
          'que quieres usar — volver a intentarlo con la misma dará el mismo resultado.'
        : 'Tu cuenta de GitHub todavía no tiene acceso al portal. Pídeselo a un administrador.'
      : null,
  );
  const [busy, setBusy] = useState(false);

  // Entrada por correo, en dos pasos dentro de la MISMA pantalla. Se pide el código aquí y
  // se teclea aquí: así la sesión queda en el navegador que la pidió. Un enlace en el
  // correo la habría creado donde se abriera el correo —el móvil, normalmente— y además se
  // gastaría solo, porque los antivirus de correo abren los enlaces para analizarlos.
  const [correo, setCorreo] = useState('');
  const [codigo, setCodigo] = useState('');
  const [codigoPedido, setCodigoPedido] = useState(false);
  const [avisoCorreo, setAvisoCorreo] = useState<string | null>(null);

  const pedirCodigo = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestEmailCode(correo.trim());
      setCodigoPedido(true);
      // El mensaje NO confirma que esa dirección tenga acceso: el servidor contesta igual
      // exista o no, y decir aquí «te lo hemos enviado» convertiría la pantalla en el
      // detector que el servidor evita ser.
      setAvisoCorreo(
        'Si esa dirección tiene acceso, en un momento llega un código de 6 dígitos. ' +
          'Caduca en 10 minutos.',
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'No se pudo pedir el código.');
    } finally {
      setBusy(false);
    }
  };

  const entrarConCodigo = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await loginWithEmailCode(correo.trim(), codigo.trim());
      await client.invalidateQueries();
      window.location.assign(volver);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'No se pudo entrar.');
      setBusy(false);
    }
  };

  const submitBootstrap = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await loginWithBootstrapToken(token.trim());
      // Se invalida todo para que la sesión nueva recargue los datos.
      await client.invalidateQueries();
      window.location.assign(volver);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'No se pudo iniciar sesión.');
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto mt-16 max-w-sm px-6">
      <h1 className="text-lg font-semibold">Software Command Center</h1>
      <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
        Portal interno. Se requiere autorización.
      </p>

      {error && (
        <div className="mt-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      {config.data?.oauthConfigured && (
        <a
          href={
            volver === '/'
              ? '/api/auth/github'
              : `/api/auth/github?volver=${encodeURIComponent(volver)}`
          }
          className="mt-6 block rounded-lg bg-[var(--color-ink)] px-4 py-2.5 text-center text-[14px] font-medium text-[var(--color-surface)]"
        >
          Entrar con GitHub
        </a>
      )}

      {config.data?.emailLoginAvailable && (
        <section className="mt-5">
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-[var(--color-border-subtle)]" />
            <span className="text-[11px] uppercase tracking-wide text-[var(--color-ink-muted)]">
              o con tu correo
            </span>
            <span className="h-px flex-1 bg-[var(--color-border-subtle)]" />
          </div>

          {!codigoPedido ? (
            <form onSubmit={(e) => void pedirCodigo(e)} className="mt-3 space-y-2">
              <label className="block">
                <span className="mb-1 block text-[12px] text-[var(--color-ink-muted)]">
                  Tu correo
                </span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                  placeholder="tu@correo.com"
                  className="w-full rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-3 py-2 text-[14px]"
                />
              </label>
              <button
                type="submit"
                disabled={busy || correo.trim().length < 3}
                className="w-full rounded-lg border border-[var(--color-border-subtle)] px-4 py-2 text-[14px] font-medium disabled:opacity-40"
              >
                {busy ? 'Enviando…' : 'Enviarme un código'}
              </button>
              <p className="text-[11px] leading-snug text-[var(--color-ink-muted)]">
                Solo funciona con el correo de una cuenta que ya tiene acceso. Esta vía no concede
                permisos nuevos.
              </p>
            </form>
          ) : (
            <form onSubmit={(e) => void entrarConCodigo(e)} className="mt-3 space-y-2">
              {avisoCorreo && (
                <p className="rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] px-3 py-2 text-[12px] leading-snug text-[var(--color-ink-muted)]">
                  {avisoCorreo}
                </p>
              )}
              <label className="block">
                <span className="mb-1 block text-[12px] text-[var(--color-ink-muted)]">
                  Código de 6 dígitos
                </span>
                <input
                  // `one-time-code` hace que el móvil lo ofrezca solo al llegar el correo.
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  autoFocus
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-3 py-2 text-center text-[20px] tracking-[0.4em]"
                />
              </label>
              <button
                type="submit"
                disabled={busy || codigo.length !== 6}
                className="w-full rounded-lg bg-[var(--color-ink)] px-4 py-2.5 text-[14px] font-medium text-[var(--color-surface)] disabled:opacity-40"
              >
                {busy ? 'Entrando…' : 'Entrar'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCodigoPedido(false);
                  setCodigo('');
                  setAvisoCorreo(null);
                  setError(null);
                }}
                className="w-full text-[12px] text-[var(--color-ink-muted)]"
              >
                usar otro correo
              </button>
            </form>
          )}
        </section>
      )}

      {config.data && !config.data.oauthConfigured && (
        <p className="mt-6 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] leading-snug text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
          <strong className="font-semibold">OAuth con GitHub no está configurado.</strong> Hace
          falta crear la OAuth App y rellenar <code>GITHUB_OAUTH_CLIENT_ID</code> y{' '}
          <code>GITHUB_OAUTH_CLIENT_SECRET</code>. Mientras tanto se puede entrar con el token de
          arranque.
        </p>
      )}

      {config.data?.bootstrapAvailable && (
        <form onSubmit={submitBootstrap} className="mt-5">
          <label
            htmlFor="bootstrap-token"
            className="block text-[12px] font-medium text-[var(--color-ink-muted)]"
          >
            Token de arranque
          </label>
          <input
            id="bootstrap-token"
            type="password"
            autoComplete="off"
            value={token}
            onChange={(event) => setToken(event.target.value)}
            className="mt-1 w-full rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-3 py-2 text-[13px] outline-none focus:border-[var(--color-ink-muted)]"
            placeholder="del fichero de configuración del servidor"
          />
          <button
            type="submit"
            disabled={busy || token.trim().length === 0}
            className="mt-2 w-full rounded-lg border border-[var(--color-border-subtle)] px-4 py-2 text-[13px] font-medium disabled:opacity-50"
          >
            {busy ? 'Entrando…' : 'Entrar'}
          </button>
          <p className="mt-2 text-[11px] leading-snug text-[var(--color-ink-muted)]">
            Acceso temporal con rol de administrador. Debe desactivarse en cuanto OAuth funcione,
            vaciando <code>SCC_BOOTSTRAP_TOKEN</code>.
          </p>
        </form>
      )}

      {config.data && !config.data.oauthConfigured && !config.data.bootstrapAvailable && (
        <p className="mt-5 text-[13px] text-[var(--color-ink-muted)]">
          No hay ningún método de acceso configurado. Revisa{' '}
          <code>/etc/software-command-center/scc.env</code>.
        </p>
      )}

      {config.data && config.data.allowlistSize === 0 && config.data.oauthConfigured && (
        <p className="mt-4 text-[12px] text-[var(--color-ink-muted)]">
          Aviso: <code>AUTH_ALLOWLIST</code> está vacía, así que nadie podrá entrar por OAuth.
        </p>
      )}
    </div>
  );
}
